'use client';

import Box from '@mui/material/Box';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { ErrorBoundary } from '@suspensive/react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import Header from '@/app/components/CommonLayout/Header';
import Sidebar from '@/app/components/CommonLayout/Sidebar';
import { ErrorBoundaryFallback } from '@/app/components/ErrorBoundary';
import PermissionsProvider from '@/app/components/Provider/PermissionsProvider';
import { HEADER, SIDEBAR } from '@/app/components/Provider/ThemeProvider/constant';
import { useDispatch } from '@/stores';
import { authActions } from '@/stores/authSlice';
import { historyActions } from '@/stores/historySlice';
import { getLoginStatus } from '@/utils/token';

const CommonLayout = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const pathname = usePathname();
  const isLoggedIn = getLoginStatus();
  const dispatch = useDispatch();

  const isAuthPage = ['/login', '/bot-preview'].some(
    (item) => pathname.split('/')[1] && item.includes(pathname.split('/')[1])
  );
  const isShowPage = isLoggedIn || ['/login', '/bot-preview'].includes(pathname);

  useEffect(() => {
    if (router) {
      const isGotoLoginPage = !isLoggedIn && !['/login', '/bot-preview'].includes(pathname);

      if (isGotoLoginPage) {
        dispatch(authActions.clearUser());
        dispatch(historyActions.clearHistory());
        router.replace(`/login${pathname !== '/' ? `?nextUrl=${encodeURIComponent(pathname.slice(1))}` : ''}`);
      }
    }
  }, [pathname, router, isLoggedIn, dispatch]);

  return !isShowPage ? (
    <Stack justifyContent="center" alignItems="center" height={1}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,

          [`.${linearProgressClasses.root}`]: {
            backgroundColor: (theme) => theme.palette.error.light,
            height: '8px',
            [`.${linearProgressClasses.bar}`]: {
              backgroundColor: (theme) => theme.palette.error.main,
            },
          },
        }}
      >
        <LinearProgress />
      </Box>
      <Image alt="logo" src="/gd-nhat-anh.png" />
    </Stack>
  ) : !isAuthPage ? (
    <PermissionsProvider>
      <Header />
      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Sidebar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: 1,
            display: 'flex',
            flexDirection: 'column',
            px: 4,
            pt: `${HEADER.H_DESKTOP + 8}px`,
            pb: 8,
            width: `calc(100% - ${SIDEBAR.WIDTH}px)`,
            position: 'relative',
            background: (theme) => theme.palette.grey[100],
            scrollPadding: 0,
          }}
        >
          <QueryErrorResetBoundary>
            {() => <ErrorBoundary fallback={ErrorBoundaryFallback}>{children}</ErrorBoundary>}
          </QueryErrorResetBoundary>
        </Box>
      </Box>
    </PermissionsProvider>
  ) : (
    children
  );
};

export default CommonLayout;

const Image = styled('img')`
  width: 40vw;
  animation: fade 2s ease-in-out infinite;

  @keyframes fade {
    0%,
    100% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
  }
`;
